import { useState, useEffect } from 'react'
import toast from 'react-hot-toast'

const EditJobModal = ({ isOpen, onClose, job }) => {
  const [templates, setTemplates] = useState([])
  const [formData, setFormData] = useState(null)
  const [selectedDays, setSelectedDays] = useState([])

  useEffect(() => {
    if (isOpen && job) {
      fetchTemplates()
      setFormData({
        name: job.name || '',
        description: job.description || '',
        status: job.status || 'pending',
        mode: job.mode || 'run_job',
        template_id: job.template_id || '',
        search_query: job.search_query || '',
        max_results: job.max_results || 10,
        urls: job.urls || [],
        schedule: job.schedule || {
          frequency: 'daily',
          time: '08:00',
          days_of_week: [],
          day_of_month: 1,
          interval_hours: 1
        },
        llm_enabled: job.llm_enabled !== undefined ? job.llm_enabled : true,
        llm_provider: job.llm_provider || 'openai',
        llm_model: job.llm_model || 'gpt-4o-mini',
        llm_api_key: job.llm_api_key || '',
        llm_extraction_prompt: job.llm_extraction_prompt || '',
        cache_enabled: job.cache_enabled || false,
        extraction_schema: job.extraction_schema || null,
        advanced_options: job.advanced_options || null,
        notifications: job.notifications || {
          email_on_success: true,
          email_on_failure: true,
          webhook_url: ''
        }
      })
      
      if (job.schedule?.days_of_week) {
        setSelectedDays(job.schedule.days_of_week)
      }
    }
  }, [isOpen, job])

  const fetchTemplates = async () => {
    try {
      const response = await fetch('/api/templates/')
      const data = await response.json()
      
      if (data.success && data.templates) {
        setTemplates(data.templates)
      } else if (data.templates) {
        setTemplates(data.templates)
      }
    } catch (error) {
      console.error('Failed to fetch templates:', error)
    }
  }

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const handleScheduleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      schedule: {
        ...prev.schedule,
        [field]: value
      }
    }))
  }

  const handleNotificationChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      notifications: {
        ...prev.notifications,
        [field]: value
      }
    }))
  }

  const toggleDay = (day) => {
    const newDays = selectedDays.includes(day)
      ? selectedDays.filter(d => d !== day)
      : [...selectedDays, day]
    setSelectedDays(newDays)
    handleScheduleChange('days_of_week', newDays)
  }

  const handleSubmit = async () => {
    if (!formData.name) {
      toast.error('Please enter a job name')
      return
    }

    try {
      const response = await fetch(`/api/jobs/${job._id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      })

      const data = await response.json()

      if (data.success) {
        toast.success('Job updated successfully!')
        onClose(true)
      } else {
        toast.error(data.message || 'Failed to update job')
      }
    } catch (error) {
      console.error('Failed to update job:', error)
      toast.error('Failed to update job')
    }
  }

  if (!isOpen || !job || !formData) return null

  const daysOfWeek = [
    { label: 'Mon', value: 0 },
    { label: 'Tue', value: 1 },
    { label: 'Wed', value: 2 },
    { label: 'Thu', value: 3 },
    { label: 'Fri', value: 4 },
    { label: 'Sat', value: 5 },
    { label: 'Sun', value: 6 }
  ]

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-8">
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-light-200/20 px-8 py-6 flex items-center justify-between z-10">
          <h2 className="text-2xl font-bold text-dark-300">Edit Scheduled Job</h2>
          <button 
            onClick={() => onClose(false)}
            className="p-2 hover:bg-light-50 rounded-lg transition-colors"
          >
            <i className="fa-solid fa-times text-dark-100 text-xl"></i>
          </button>
        </div>

        {/* Content */}
        <div className="p-8 space-y-6">
          {/* Job Name */}
          <section>
            <label className="block mb-2">
              <span className="text-sm font-bold text-dark-300">Job Name <span className="text-primary-500">*</span></span>
            </label>
            <input 
              type="text" 
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              className="w-full px-4 py-3 border-2 border-light-200/30 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </section>

          {/* Description */}
          <section>
            <label className="block mb-2">
              <span className="text-sm font-bold text-dark-300">Description</span>
            </label>
            <textarea 
              rows="2"
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              className="w-full px-4 py-3 border-2 border-light-200/30 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </section>

          {/* Status */}
          <section>
            <label className="block mb-2">
              <span className="text-sm font-bold text-dark-300">Status</span>
            </label>
            <select 
              value={formData.status}
              onChange={(e) => handleInputChange('status', e.target.value)}
              className="w-full px-4 py-3 border-2 border-light-200/30 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="active">Active</option>
              <option value="paused">Paused</option>
              <option value="pending">Pending</option>
            </select>
          </section>

          {/* Template Selection */}
          {formData.mode === 'run_job' && (
            <section>
              <label className="block mb-2">
                <span className="text-sm font-bold text-dark-300">Select Template</span>
              </label>
              <select 
                value={formData.template_id}
                onChange={(e) => handleInputChange('template_id', e.target.value)}
                className="w-full px-4 py-3 border-2 border-light-200/30 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="">Choose a template...</option>
                {templates.map(template => (
                  <option key={template._id} value={template._id}>
                    {template.name} ({template.schema ? Object.keys(template.schema).length : 0} fields)
                  </option>
                ))}
              </select>
            </section>
          )}

          {/* Search Query */}
          {(formData.mode === 'auto' || formData.mode === 'run_job') && (
            <section>
              <label className="block mb-2">
                <span className="text-sm font-bold text-dark-300">Search Query</span>
              </label>
              <textarea 
                rows="2"
                value={formData.search_query}
                onChange={(e) => handleInputChange('search_query', e.target.value)}
                className="w-full px-4 py-3 border-2 border-light-200/30 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </section>
          )}

          {/* Schedule Configuration */}
          <section className="bg-light-50 rounded-xl p-6 border border-light-200/20">
            <h3 className="text-lg font-bold text-dark-300 mb-4">Schedule Configuration</h3>

            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block mb-2">
                  <span className="text-sm font-bold text-dark-300">Frequency <span className="text-primary-500">*</span></span>
                </label>
                <select 
                  value={formData.schedule.frequency}
                  onChange={(e) => handleScheduleChange('frequency', e.target.value)}
                  className="w-full px-4 py-3 border-2 border-light-200/30 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="hourly">Hourly</option>
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                  <option value="custom">Custom (Cron Expression)</option>
                </select>
              </div>

              <div>
                <label className="block mb-2">
                  <span className="text-sm font-bold text-dark-300">Time (UTC) <span className="text-primary-500">*</span></span>
                </label>
                <input 
                  type="time"
                  value={formData.schedule.time}
                  onChange={(e) => handleScheduleChange('time', e.target.value)}
                  className="w-full px-4 py-3 border-2 border-light-200/30 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Days of Week (for Weekly) */}
            {formData.schedule.frequency === 'weekly' && (
              <div className="mb-4">
                <label className="block mb-2">
                  <span className="text-sm font-bold text-dark-300">Days of Week</span>
                </label>
                <div className="flex space-x-2">
                  {daysOfWeek.map(day => (
                    <button
                      key={day.value}
                      type="button"
                      onClick={() => toggleDay(day.value)}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                        selectedDays.includes(day.value)
                          ? 'gradient-primary text-white'
                          : 'bg-light-100 hover:bg-light-200/30 text-dark-300'
                      }`}
                    >
                      {day.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Day of Month (for Monthly) */}
            {formData.schedule.frequency === 'monthly' && (
              <div className="mb-4">
                <label className="block mb-2">
                  <span className="text-sm font-bold text-dark-300">Day of Month (1-31)</span>
                </label>
                <input 
                  type="number"
                  min="1"
                  max="31"
                  value={formData.schedule.day_of_month}
                  onChange={(e) => handleScheduleChange('day_of_month', parseInt(e.target.value))}
                  className="w-full px-4 py-3 border-2 border-light-200/30 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
            )}

            {/* Interval Hours (for Hourly) */}
            {formData.schedule.frequency === 'hourly' && (
              <div className="mb-4">
                <label className="block mb-2">
                  <span className="text-sm font-bold text-dark-300">Interval (Hours)</span>
                </label>
                <input 
                  type="number"
                  min="1"
                  max="24"
                  value={formData.schedule.interval_hours}
                  onChange={(e) => handleScheduleChange('interval_hours', parseInt(e.target.value))}
                  className="w-full px-4 py-3 border-2 border-light-200/30 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
            )}
          </section>

          {/* Extraction Configuration */}
          <section className="bg-light-50 rounded-xl p-6 border border-light-200/20">
            <h3 className="text-lg font-bold text-dark-300 mb-4">Extraction Configuration</h3>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block mb-2">
                  <span className="text-sm font-bold text-dark-300">Max Results</span>
                </label>
                <input 
                  type="number"
                  min="1"
                  max="200"
                  value={formData.max_results}
                  onChange={(e) => handleInputChange('max_results', parseInt(e.target.value))}
                  className="w-full px-4 py-3 border-2 border-light-200/30 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block mb-2">
                  <span className="text-sm font-bold text-dark-300">LLM Model</span>
                </label>
                <select 
                  value={formData.llm_model}
                  onChange={(e) => handleInputChange('llm_model', e.target.value)}
                  className="w-full px-4 py-3 border-2 border-light-200/30 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="gpt-4o-mini">GPT-4o-mini (Fast & Cheap)</option>
                  <option value="gpt-4o">GPT-4o (Best Quality)</option>
                  <option value="claude-3-5-sonnet-20241022">Claude 3.5 Sonnet</option>
                  <option value="claude-3-haiku-20240307">Claude 3 Haiku (Fast)</option>
                </select>
              </div>
            </div>
          </section>

          {/* Notifications */}
          <section className="bg-light-50 rounded-xl p-6 border border-light-200/20">
            <h3 className="text-lg font-bold text-dark-300 mb-4">Notifications & Alerts</h3>

            <div className="space-y-3">
              <div className="flex items-center justify-between p-4 bg-white rounded-lg border border-light-200/20">
                <div>
                  <div className="text-sm font-semibold text-dark-300">Email on Success</div>
                  <div className="text-xs text-dark-100">Send email when job completes successfully</div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input 
                    type="checkbox"
                    checked={formData.notifications.email_on_success}
                    onChange={(e) => handleNotificationChange('email_on_success', e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                </label>
              </div>

              <div className="flex items-center justify-between p-4 bg-white rounded-lg border border-light-200/20">
                <div>
                  <div className="text-sm font-semibold text-dark-300">Email on Failure</div>
                  <div className="text-xs text-dark-100">Send email when job fails</div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input 
                    type="checkbox"
                    checked={formData.notifications.email_on_failure}
                    onChange={(e) => handleNotificationChange('email_on_failure', e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                </label>
              </div>

              <div className="p-4 bg-white rounded-lg border border-light-200/20">
                <div className="mb-2">
                  <div className="text-sm font-semibold text-dark-300">Webhook URL (Optional)</div>
                  <div className="text-xs text-dark-100">Send webhook POST request after job completion</div>
                </div>
                <input 
                  type="url"
                  placeholder="https://your-webhook-url.com/endpoint"
                  value={formData.notifications.webhook_url}
                  onChange={(e) => handleNotificationChange('webhook_url', e.target.value)}
                  className="w-full px-4 py-2 border border-light-200/30 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                />
              </div>
            </div>
          </section>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-white border-t border-light-200/20 px-8 py-6 flex items-center justify-end space-x-3">
          <button 
            onClick={() => onClose(false)}
            className="px-6 py-3 bg-light-100 hover:bg-light-200/30 rounded-lg font-semibold text-dark-300 transition-colors"
          >
            Cancel
          </button>
          <button 
            onClick={handleSubmit}
            className="px-6 py-3 gradient-primary hover:opacity-90 text-white rounded-lg font-semibold transition-all shadow-md flex items-center"
          >
            <i className="fa-solid fa-save mr-2"></i>
            Save Changes
          </button>
        </div>
      </div>
    </div>
  )
}

export default EditJobModal

